<script setup lang="ts">
import type { UserAttrType } from '@/types/user'
import { useUserStore } from '@/stores/modules/user'
import { formatNumber } from '@/utils'
import { noPercentEquipAttrKeyList } from '@/views/item/data'

const userStore = useUserStore()

defineProps<{ realUserAttr?: Partial<UserAttrType> }>()

function getRealSpecAttr(attr: number) {
  return (attr / (attr + 100)) * 100
}

function getDifAttr(key: keyof UserAttrType, attr: number, baseAttr: number, divisor = 1, precision = 2) {
  const dif = (attr - baseAttr) / divisor
  if (Math.abs(dif) < 1 / Math.pow(10, precision)) return ''

  const sign = dif > 0 ? '+' : '-'
  const absDif = formatNumber(Math.abs(dif), precision)
  const cls = dif > 0 ? 'grow' : 'shrink'

  return `<span class="diff-tag ${cls}">${sign}${absDif}${noPercentEquipAttrKeyList.includes(key) ? '' : '%'}</span>`
}

function getDifClass(attr: number, baseAttr: number) {
  const dif = attr - baseAttr
  if (dif === 0) return ''
  return dif > 0 ? 'grow' : 'shrink'
}
</script>

<template>
  <el-popover placement="top" :width="300" trigger="click" popper-class="attr-popover">
    <template #reference>
      <el-button size="small" plain type="primary" class="attr-btn">实时属性</el-button>
    </template>
    <div class="attr-comparison" v-if="realUserAttr">
      <div class="text-wrap border-wrap affix">
        <p>
          <span>攻击：</span>
          <span>
            <span>{{ realUserAttr.atk }}</span>
            <span v-html="getDifAttr('atk', realUserAttr.atk || 0, userStore.userAttr.atk)"></span>
          </span>
        </p>
        <p>
          <span>攻击速度：</span>
          <span
            ><span>{{ ((realUserAttr.atksp || 0) / 100).toFixed(3) }}</span>
            <span v-html="getDifAttr('atksp', realUserAttr.atksp || 0, userStore.userAttr.atksp, 100, 3)"></span>
          </span>
        </p>
        <p>
          <span>暴击率：</span>
          <span>
            <span>{{ realUserAttr.crt }}%</span>
            <span v-html="getDifAttr('crt', realUserAttr.crt || 0, userStore.userAttr.crt)"></span>
          </span>
        </p>
        <p>
          <span>暴击伤害：</span>
          <span>
            <span>{{ realUserAttr.crtd }}%</span>
            <span v-html="getDifAttr('crtd', realUserAttr.crtd || 0, userStore.getRealCrtd(), 1, 1)"></span>
          </span>
        </p>
        <p>
          <span>破防：</span>
          <span>
            <span>{{ realUserAttr.heat }}</span>
            <span v-html="getDifAttr('heat', realUserAttr.heat || 0, userStore.userAttr.heat)"></span>
          </span>
        </p>
        <p>
          <span>命中率：</span>
          <span>
            <span>{{ realUserAttr.hr }}%</span>
            <span v-html="getDifAttr('hr', realUserAttr.hr || 0, userStore.userAttr.hr)"></span>
          </span>
        </p>
        <p>
          <span>抗魔值：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.voidDef || 0, 2) }}</span>
            <span v-html="getDifAttr('voidDef', realUserAttr.voidDef || 0, userStore.userAttr.voidDef)"></span>
          </span>
        </p>
        <p>
          <span>全伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.ad || 0, 2) }}%</span>
            <span v-html="getDifAttr('ad', realUserAttr.ad || 0, userStore.userAttr.ad)"></span>
          </span>
        </p>

        <template v-if="userStore.soulType > 3">
          <p>
            <span>魔法值：</span>
            <span>
              <span>{{ realUserAttr.mp }}</span>
              <span v-html="getDifAttr('mp', realUserAttr.mp || 0, userStore.userAttr.mp)"></span>
            </span>
          </p>
          <p :class="getDifClass(realUserAttr.mpRegen || 0, userStore.userAttr.mpRegen)">
            <span>魔法回复：</span>
            <span>
              <span>{{ realUserAttr.mpRegen }}</span>
              <span v-html="getDifAttr('mpRegen', realUserAttr.mpRegen || 0, userStore.userAttr.mpRegen)"></span>
            </span>
          </p>
          <p>
            <span>冷却缩减：</span>
            <span>
              <span>{{ formatNumber(realUserAttr.cda || 0, 2) }} ({{ formatNumber(getRealSpecAttr(realUserAttr.cda || 0), 2) }}%)</span>
              <span v-html="getDifAttr('cda', realUserAttr.cda || 0, getRealSpecAttr(userStore.userAttr.cda))"></span>
            </span>
          </p>
          <p>
            <span>法消降低：</span>
            <span>
              <span>{{ formatNumber(realUserAttr.sce || 0, 2) }} ({{ formatNumber(getRealSpecAttr(realUserAttr.sce || 0), 2) }}%)</span>
              <span v-html="getDifAttr('sce', realUserAttr.sce || 0, getRealSpecAttr(userStore.userAttr.sce))"></span>
            </span>
          </p>
          <p>
            <span>技能伤害加成：</span>
            <span>
              <span>{{ formatNumber(realUserAttr.skillDa || 0, 2) }}%</span>
              <span v-html="getDifAttr('skillDa', realUserAttr.skillDa || 0, userStore.userAttr.skillDa)"></span>
            </span>
          </p>
        </template>
        <template v-if="userStore.advanceLevel === 4">
          <p>
            <span>风伤害加成：</span>
            <span>
              <span>{{ formatNumber(realUserAttr.windDa || 0, 2) }}%</span>
              <span v-html="getDifAttr('windDa', realUserAttr.windDa || 0, userStore.userAttr.windDa)"></span>
            </span>
          </p>
          <p>
            <span>火伤害加成：</span>
            <span>
              <span>{{ formatNumber(realUserAttr.fireDa || 0, 2) }}%</span>
              <span v-html="getDifAttr('fireDa', realUserAttr.fireDa || 0, userStore.userAttr.fireDa)"></span>
            </span>
          </p>
          <p>
            <span>水伤害加成：</span>
            <span>
              <span>{{ formatNumber(realUserAttr.waterDa || 0, 2) }}%</span>
              <span v-html="getDifAttr('waterDa', realUserAttr.waterDa || 0, userStore.userAttr.waterDa)"></span>
            </span>
          </p>
          <p>
            <span>土伤害加成：</span>
            <span>
              <span>{{ formatNumber(realUserAttr.soilDa || 0, 2) }}%</span>
              <span v-html="getDifAttr('soilDa', realUserAttr.soilDa || 0, userStore.userAttr.soilDa)"></span>
            </span>
          </p>
        </template>
        <p>
          <span>独立风伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.windIndDa || 0) }}%</span>
            <span v-html="getDifAttr('windIndDa', realUserAttr.windIndDa || 0, userStore.userAttr.windIndDa)"></span>
          </span>
        </p>
        <p>
          <span>独立火伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.fireIndDa || 0) }}%</span>
            <span v-html="getDifAttr('fireIndDa', realUserAttr.fireIndDa || 0, userStore.userAttr.fireIndDa)"></span>
          </span>
        </p>
        <p>
          <span>独立水伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.waterIndDa || 0) }}%</span>
            <span v-html="getDifAttr('waterIndDa', realUserAttr.waterIndDa || 0, userStore.userAttr.waterIndDa)"></span>
          </span>
        </p>
        <p>
          <span>独立土伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.soilIndDa || 0) }}%</span>
            <span v-html="getDifAttr('soilIndDa', realUserAttr.soilIndDa || 0, userStore.userAttr.soilIndDa)"></span>
          </span>
        </p>
        <p>
          <span>全技能等级加成：</span>
          <span>
            <span>{{ realUserAttr.allSkillGrow || 0 }}</span>
            <span v-html="getDifAttr('allSkillGrow', realUserAttr.allSkillGrow || 0, userStore.userAttr.allSkillGrow)"></span>
          </span>
        </p>
        <p>
          <span>风系技能等级加成：</span>
          <span>
            <span>{{ realUserAttr.windSkillGrow || 0 }}</span>
            <span v-html="getDifAttr('windSkillGrow', realUserAttr.windSkillGrow || 0, userStore.userAttr.windSkillGrow)"></span>
          </span>
        </p>
        <p>
          <span>火系技能等级加成：</span>
          <span>
            <span>{{ realUserAttr.fireSkillGrow || 0 }}</span>
            <span v-html="getDifAttr('fireSkillGrow', realUserAttr.fireSkillGrow || 0, userStore.userAttr.fireSkillGrow)"></span>
          </span>
        </p>
        <p>
          <span>水系技能等级加成：</span>
          <span>
            <span>{{ realUserAttr.waterSkillGrow || 0 }}</span>
            <span v-html="getDifAttr('waterSkillGrow', realUserAttr.waterSkillGrow || 0, userStore.userAttr.waterSkillGrow)"></span>
          </span>
        </p>
        <p>
          <span>土系技能等级加成：</span>
          <span>
            <span>{{ realUserAttr.soilSkillGrow || 0 }}</span>
            <span v-html="getDifAttr('soilSkillGrow', realUserAttr.soilSkillGrow || 0, userStore.userAttr.soilSkillGrow)"></span>
          </span>
        </p>
        <p>
          <span>对不死族伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.undeadDa || 0, 2) }}%</span>
            <span v-html="getDifAttr('undeadDa', realUserAttr.undeadDa || 0, userStore.userAttr.undeadDa)"></span>
          </span>
        </p>
        <p>
          <span>对兽族伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.orcDa || 0, 2) }}%</span>
            <span v-html="getDifAttr('orcDa', realUserAttr.orcDa || 0, userStore.userAttr.orcDa)"></span>
          </span>
        </p>
        <p>
          <span>对穴居族伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.caveDa || 0, 2) }}%</span>
            <span v-html="getDifAttr('caveDa', realUserAttr.caveDa || 0, userStore.userAttr.caveDa)"></span>
          </span>
        </p>
        <p>
          <span>对元素族伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.elDa || 0, 2) }}%</span>
            <span v-html="getDifAttr('elDa', realUserAttr.elDa || 0, userStore.userAttr.elDa)"></span>
          </span>
        </p>
        <p>
          <span>对精灵伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.elfDa || 0, 2) }}%</span>
            <span v-html="getDifAttr('elfDa', realUserAttr.elfDa || 0, userStore.userAttr.elfDa)"></span>
          </span>
        </p>
        <p>
          <span>对恶魔伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.dmDa || 0, 2) }}%</span>
            <span v-html="getDifAttr('dmDa', realUserAttr.dmDa || 0, userStore.userAttr.dmDa)"></span>
          </span>
        </p>
        <p>
          <span>对妖精伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.faeDa || 0, 2) }}%</span>
            <span v-html="getDifAttr('faeDa', realUserAttr.faeDa || 0, userStore.userAttr.faeDa)"></span>
          </span>
        </p>
        <p>
          <span>对泥泽族伤害加成：</span>
          <span>
            <span>{{ formatNumber(realUserAttr.mzDa || 0, 2) }}%</span>
            <span v-html="getDifAttr('mzDa', realUserAttr.mzDa || 0, userStore.userAttr.mzDa)"></span>
          </span>
        </p>
      </div>
    </div>
    <div v-else class="empty-tip">暂无战斗数据</div>
  </el-popover>
</template>

<style lang="scss" scoped>
.attr-btn {
  position: absolute;
  left: 4px;
  top: 5px;
  z-index: 1;
}

.text-wrap {
  max-height: 50vh;
  max-width: 300px;
  overflow: auto;

  p {
    font-size: 14px;
    margin: 4px 0;
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }
}

.border-wrap {
  padding: 10px;
}

:deep(.diff-tag) {
  margin-left: 5px;
  padding: 0 4px;
  color: var(--fontColor) !important;
  border-radius: 3px;
}

:deep(.diff-tag.grow) {
  background-color: hsla(99, 44%, 63%, 0.5);
}

:deep(.diff-tag.shrink) {
  background-color: hsla(9, 77%, 47%, 0.5);
}
</style>
